/**
 * OpenAIBillingConnectModal — paste-token form for the OpenAI org-admin
 * billing connector. The user pastes a single `sk-admin-...` key minted
 * from platform.openai.com/settings/organization/admin-keys; backend
 * validates it against the /v1/organization/costs probe before
 * persisting (see handlers/llm-billing.js).
 *
 * Pattern lifted from LarkConnectModal — same shell, same reset-on-open
 * behavior, same stopPropagation guards for nested-modal callers. The
 * only differences are the help text + the single-field form layout.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectOpenAIBilling } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function OpenAIBillingConnectModal({ isOpen, onClose, onConnected }) {
  const [adminKey, setAdminKey] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectOpenAIBilling();

  useEffect(() => {
    if (isOpen) {
      setAdminKey('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!adminKey.trim()) {
      setError('Admin key is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { adminKey: adminKey.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect OpenAI Admin. Please check your admin key.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect OpenAI Admin"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect OpenAI Admin</h2>
        <p className="lark-modal-description">
          Get your admin key from{' '}
          <a
            href="https://platform.openai.com/settings/organization/admin-keys"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--ion-color-primary)' }}
          >
            platform.openai.com/settings/organization/admin-keys
          </a>{' '}
          (requires org admin role). Zibby reads daily org-wide spend via{' '}
          <code>/v1/organization/costs</code>.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="openai-billing-admin-key">Admin Key</label>
          <input
            id="openai-billing-admin-key"
            type="password"
            value={adminKey}
            placeholder="sk-admin-..."
            onChange={(e) => { setAdminKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !adminKey.trim()}
            aria-label="Submit OpenAI Admin admin key"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel OpenAI Admin connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
